/**
 * How a lesson's constellation is lit, segment by segment.
 *
 * The constellation is one line through a lesson's concepts in teaching
 * order (drawn by `MindspaceGraph.drawConstellations`). Each segment shows
 * whether the student holds both of the ideas it joins, so a lesson visibly
 * fills in as it is learned and slips back as it is forgotten.
 */

import type { MemoryTier } from '@/lib/mindspace/memoryTier';

/**
 * A constellation segment's state from the two concepts it joins: lit when
 * both are learned, slipping when either is fading or due, unlit when either
 * is not studied (or paused, which is on hold rather than learned).
 */
export function segmentState(
  a: { tier?: MemoryTier; isPaused?: boolean },
  b: { tier?: MemoryTier; isPaused?: boolean }
): 'lit' | 'slipping' | 'unlit' {
  const ends = [a, b];
  if (ends.some(n => n.isPaused || n.tier === 'new')) return 'unlit';
  if (ends.some(n => n.tier === 'fading' || n.tier === 'due'))
    return 'slipping';
  return 'lit';
}
